/**
 * Euclidean rhythms — Bjorklund's algorithm.
 *
 * Distribute `pulses` onsets across `steps` slots as evenly as possible. The
 * recursion is the same one Euclid used for the greatest common divisor, which
 * is where the name comes from: repeatedly fold the remainder back into the
 * pattern until nothing is left to fold.
 *
 * Toussaint's observation is that the result is not a curiosity — it lands on
 * rhythms that already exist. E(3,8) is the Cuban tresillo, E(5,8) the
 * cinquillo, E(2,5) a Persian khafif-e-ramal, E(7,16) a Brazilian samba
 * necklace. One integer pair reproduces each of them.
 *
 * Pure, dependency-free, and deterministic — no rendering concerns here.
 */

/**
 * @returns a boolean array of length `steps`, true where an onset falls.
 */
export function euclid(pulses: number, steps: number): boolean[] {
    const n = Math.max(0, Math.floor(steps));
    const k = Math.min(Math.max(0, Math.floor(pulses)), n);
    if (n === 0) return [];
    if (k === 0) return new Array(n).fill(false);
    if (k === n) return new Array(n).fill(true);

    // Start with k groups of [true] and (n-k) groups of [false], then repeatedly
    // distribute the smaller pile into the larger one.
    let a: boolean[][] = Array.from({ length: k }, () => [true]);
    let b: boolean[][] = Array.from({ length: n - k }, () => [false]);

    while (b.length > 1) {
        const pairs = Math.min(a.length, b.length);
        const merged: boolean[][] = [];
        for (let i = 0; i < pairs; i++) merged.push([...a[i], ...b[i]]);

        // whichever pile had leftovers becomes the new remainder
        const restA = a.slice(pairs);
        const restB = b.slice(pairs);
        const rest = restA.length ? restA : restB;

        a = merged;
        b = rest;
    }

    return [...a, ...b].flat();
}

/** Indices of the onsets, which is usually what a renderer actually wants. */
export function euclidOnsets(pulses: number, steps: number): number[] {
    const pattern = euclid(pulses, steps);
    const out: number[] = [];
    for (let i = 0; i < pattern.length; i++) if (pattern[i]) out.push(i);
    return out;
}

/**
 * Rotate a pattern. Traditional rhythms are frequently a rotation of the
 * canonical Euclidean necklace rather than the necklace itself.
 */
export function rotate<T>(xs: T[], by: number): T[] {
    if (!xs.length) return xs;
    const k = ((by % xs.length) + xs.length) % xs.length;
    return [...xs.slice(k), ...xs.slice(0, k)];
}

export interface RhythmVoice {
    /** Slots in the cycle */
    steps: number;
    /** Onsets to distribute across them */
    pulses: number;
    /** Rotation applied after generation */
    rotation?: number;
    /** Frequency in Hz, if this voice is ever sounded */
    freq: number;
    color: string;
    /** What this integer pair is, in music */
    name: string;
}

/**
 * Just-intonation ratios against a 220 Hz root. These are the intervals the
 * ear reads as consonant because their frequency ratios are small integers —
 * the same reason the rhythms above feel even.
 */
export const A3 = 220;
export const RATIO = {
    unison: 1,
    minorThird: 6 / 5,
    majorThird: 5 / 4,
    fourth: 4 / 3,
    fifth: 3 / 2,
    octave: 2,
} as const;
